import { TrackingProvider } from '@act/react-components/tracking';
import {
  Box,
  Typography,
  useTheme,
  Button,
  SxProps,
  Theme,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { Summary } from './Summary';
import { VoiceCallRecordingDto } from '@act/shared/data-transfer-objects';
import BubbleChat from '../../../post-call-summary-drawer/BubbleTranscript';
import { getTranscriptLinesForDisplay } from '../../../post-call-summary-drawer/transcription.utils';
import { ReemaThemeV2 } from '@act/storybook';

type ViewType = 'summary' | 'transcript';

interface ViewToggleButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const ViewToggleButton = ({
  label,
  isActive,
  onClick,
}: ViewToggleButtonProps) => {
  const theme = useTheme();

  const buttonStyles: SxProps<Theme> = useMemo(
    () => ({
      borderRadius: '20px',
      padding: '8px 16px',
      textTransform: 'none',
      backgroundColor: isActive
        ? theme.palette.navyShades[400]
        : theme.palette.strokePrimary,
      color: isActive
        ? theme.palette.primary.contrastText
        : theme.palette.text.primary,
      '&:hover, &:focus, &:active': {
        backgroundColor: isActive
          ? theme.palette.navyShades[400]
          : theme.palette.strokePrimary,
      },
    }),
    [isActive, theme]
  );

  return (
    <Button onClick={onClick} sx={buttonStyles}>
      <Typography variant="bodyMedium">{label}</Typography>
    </Button>
  );
};

export const SummaryAndTranscripts = ({
  voiceCallRecording,
}: {
  voiceCallRecording: VoiceCallRecordingDto;
}) => {
  const [activeView, setActiveView] = useState<ViewType>('summary');

  const transcriptLines = useMemo(() => {
    const recordingTranscription =
      voiceCallRecording?.recordingTranscription ?? '';
    return getTranscriptLinesForDisplay(recordingTranscription);
  }, [voiceCallRecording?.recordingTranscription]);

  return (
    <TrackingProvider contextName="summary-and-transcripts">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            gap: 1,
            zIndex: 1,
            paddingLeft: 2,
            paddingRight: 2,
            backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.creamShades[10],
          }}
        >
          <ViewToggleButton
            label="Generated notes"
            isActive={activeView === 'summary'}
            onClick={() => setActiveView('summary')}
          />
          <ViewToggleButton
            label="Transcript"
            isActive={activeView === 'transcript'}
            onClick={() => setActiveView('transcript')}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            paddingTop: '52px',
          }}
        >
          {activeView === 'summary' && (
            <Summary callRecording={voiceCallRecording} />
          )}
          {activeView === 'transcript' && (
            <Box
              sx={{
                padding: 2,
                overflow: 'auto',
                height: '100%',
              }}
            >
              <BubbleChat transcriptLines={transcriptLines} />
            </Box>
          )}
        </Box>
      </Box>
    </TrackingProvider>
  );
};
